import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { produce } from "immer";

import { NewWizardPhotoModal } from "shared/components/Modals/NewWizardPhotoModal";
import { UploadMedia } from "shared/components/UploadMedia";
import { WizardData } from "shared/components/WizardNew/types";

import { SPLIT_TEST } from "shared/constants/split";
import { DEFAULT_MODE_WIZARD, SAFE_MODE_WIZARD } from "shared/constants/wizard";

import { WizardStore, WizardType } from "shared/context/WizardStoreContext";

import { simplyClick } from "shared/helpers/analytics";
import { isSplitTestRequired } from "shared/helpers/view";
import { setStepperState } from "shared/helpers/wizard";

import { useWizard, WizardStep } from "shared/hooks/useWizard";

type Props = { wizardData: WizardData };

const WizardAddPhoto = ({ wizardData }: Props) => {
  const navigate = useNavigate();
  const wizard = useWizard(wizardData);

  const { mode } = wizardData;

  const isSplitReducedWizardEnabled =
    mode === DEFAULT_MODE_WIZARD &&
    isSplitTestRequired(wizard.data, SPLIT_TEST.reducedWizard);

  const step = wizard[WizardStep.AddPhoto];

  const [wizardStore, setWizardStore] = useContext(WizardStore);

  const setIsUploaded = (value: boolean) => {
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.addPhoto.isPhotoAdded = value;
      })
    );
  };

  const handleCloseAttractiveModal = () => {
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.addPhoto.isSkippedWithoutPhoto = false;
      })
    );

    step.analytics?.modalClose?.forEach((item) => {
      simplyClick(item, null, false);
    });
  };

  const handleAddPhotoModal = () => {
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.addPhoto.isSkippedWithoutPhoto = false;
      })
    );

    step.analytics?.modalAdd?.forEach((item) => {
      simplyClick(item, null, false);
    });
  };

  const handleSkipAddAvatar = () => {
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.addPhoto.isSkippedWithoutPhoto = false;
      })
    );

    step.analytics?.modalLater?.forEach((item) => {
      simplyClick(item, null, false);
    });

    navigate(`/${step.nextStep}`);
  };

  const handleLoad = () => {
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.addPhoto.isLoading = true;
      })
    );
  };
  const handleLoaded = () => {
    setWizardStore(
      produce(wizardStore, (wizard: WizardType) => {
        wizard.addPhoto.isLoading = false;
      })
    );
  };

  useEffect(() => {
    if (mode === SAFE_MODE_WIZARD || isSplitReducedWizardEnabled) {
      const updatedWizardStore = setStepperState(
        wizardStore,
        5,
        mode,
        isSplitReducedWizardEnabled
      ) as unknown as WizardType;

      if (JSON.stringify(wizardStore) !== JSON.stringify(updatedWizardStore)) {
        setWizardStore(updatedWizardStore);
      }
    }
  }, [mode, wizardStore]);

  return (
    <>
      <UploadMedia
        analytics={step.analytics}
        onLoad={handleLoad}
        title={"Share your smile"}
        onLoaded={handleLoaded}
        setIsUploaded={setIsUploaded}
      />

      {wizardStore.addPhoto.isSkippedWithoutPhoto && (
        <NewWizardPhotoModal
          close={handleCloseAttractiveModal}
          uploadPhoto={handleAddPhotoModal}
          addPhotoLater={handleSkipAddAvatar}
        />
      )}
    </>
  );
};

export { WizardAddPhoto };
